import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { TestAnalysisActions } from './testAnalysis.actions';
import { TestAnalysisStateModel } from './testAnalysis.state.model';
import { patch, removeItem } from '@ngxs/store/operators';
import { TestAnalysisApiService } from 'src/app/services/api/test-analysis-api.service';
import { BaseTestItemDto } from 'src/app/models/dto/Base-Test-item-dto';
import { EMPTY, last, map, Observable, scan, tap } from 'rxjs';
import { environment } from 'src/environments/environment';

@State<TestAnalysisStateModel>({
  name: 'TestAnalysis',
  defaults: {
    TestAnalysis: [],
    orgGroupTestAnalysis: [],
    functionalGroupTestAnalysis: [],
    myTestAnalysis: []
  },
})
@Injectable()
export class TestAnalysisState {
  constructor(/*private itemsService: ItemsService, */private testAnalysisApiService: TestAnalysisApiService) {}

  @Action(TestAnalysisActions.SetAllTestAnalysis)
  SetAllTestAnalysis(ctx: StateContext<TestAnalysisStateModel>, action: TestAnalysisActions.SetAllTestAnalysis) {
    const state = ctx.getState();
    //don't update state if we have already downloaded items
    if (state.TestAnalysis !== undefined && state.TestAnalysis.length > 0) return EMPTY;

    return this.testAnalysisApiService.getAll(action.company, '', '').pipe(
      tap((TestAnalysis) => {
        ctx.patchState({ TestAnalysis: TestAnalysis });
      })
    );
  }
  @Action(TestAnalysisActions.SetAllTestAnalysisPaginated)
  SetAllTestAnalysisPaginated(ctx: StateContext<TestAnalysisStateModel>, action: TestAnalysisActions.SetAllTestAnalysisPaginated): Observable<void> {
    const state = ctx.getState();
    //don't update state if we have already downloaded items
    if (state.TestAnalysis !== undefined && state.TestAnalysis.length > 0) return EMPTY;

    const pageSize = 1000;
    return this.testAnalysisApiService
      .getAllPaginatedPages(action.company, '', pageSize, '')
      .pipe(
        // accumulate progressively; emits after EVERY page
        scan((all, pageItems) => all.concat(pageItems), [] as BaseTestItemDto[]),

        // patch on EVERY emission
        tap(all => {
          // console.log('Fetched so far:', all.length);// for debug
          ctx.patchState({ TestAnalysis: all });
          if(environment.IsHero){
            ctx.patchState({ orgGroupTestAnalysis: all });
          }
        }),
        last(),// complete when the service finishes paging
        map(() => void 0)
      );
  }

  @Action(TestAnalysisActions.SetOrgGroupTestAnalysis)
  SetOrgGroupTestAnalysis(ctx: StateContext<TestAnalysisStateModel>, action: TestAnalysisActions.SetOrgGroupTestAnalysis) {
    const state = ctx.getState();
    //don't update state if we have already downloaded items
    if (state.orgGroupTestAnalysis !== undefined && state.orgGroupTestAnalysis.length > 0)
      return EMPTY;

    return this.testAnalysisApiService.getAll('', '', action.orgGroupId ).pipe(
      tap(result =>{
        ctx.patchState({ orgGroupTestAnalysis: result });
      })
    );
  }

  @Action(TestAnalysisActions.SetMyTestAnalysis)
  SetMyTestAnalysis(ctx: StateContext<TestAnalysisStateModel>, action: TestAnalysisActions.SetMyTestAnalysis) {
    const state = ctx.getState();
    //don't update state if we have already downloaded items
    if (state.myTestAnalysis !== undefined && state.myTestAnalysis.length > 0)
      return EMPTY;

    return this.testAnalysisApiService.getAll('', action.userId, '').pipe(
      tap(result =>{
        ctx.patchState({ myTestAnalysis: result });
      })
    );
  }

  @Action(TestAnalysisActions.SetFunctionalGroupTestAnalysis)
  SetFunctionalGroupTestAnalysis(ctx: StateContext<TestAnalysisStateModel>, action: TestAnalysisActions.SetFunctionalGroupTestAnalysis) {
    const state = ctx.getState();
    //don't update state if we have already downloaded items
    // if (!state.updateNeeded && state.functionalGroupTestAnalysis !== undefined && state.functionalGroupTestAnalysis.length > 0) 
    //   return;

    // for (let index = 0; index < action.functionalGroupIds.length; index++) {
    //   const fg = action.functionalGroupIds[index];
    //   this.testAnalysisApiService.getAll('', fg ).subscribe(result =>{
    //     if(result && result.length > 0) {
    //       ctx.patchState({ functionalGroupTestAnalysis: result });
    //       return;
    //     }
    //   });
    // }
  }

  @Action(TestAnalysisActions.DeleteTestAnalysis)
  DeleteTestAnalysis(ctx: StateContext<TestAnalysisStateModel>, action: TestAnalysisActions.DeleteTestAnalysis){
    return this.testAnalysisApiService.delete(action.testAnalysisUid, action.regBy).pipe(
      tap(result =>{
        ctx.setState(
          patch<TestAnalysisStateModel>({
            orgGroupTestAnalysis: removeItem<BaseTestItemDto>(x => x.uid === action.testAnalysisUid),
            TestAnalysis: removeItem<BaseTestItemDto>(x => x.uid === action.testAnalysisUid)
          })
        )
      })
    );
  }

  @Action(TestAnalysisActions.UpdateItem)
  updateItem(ctx: StateContext<TestAnalysisStateModel>, action: TestAnalysisActions.UpdateItem) {
    const state = ctx.getState();

    // Update in TestAnalysis (All)
    const allIdx = state.TestAnalysis.findIndex(item => item.uid === action.item.uid);
    if (allIdx >= 0) {
      const updated = [...state.TestAnalysis];
      updated[allIdx] = action.item;  // Full replace
      ctx.patchState({ TestAnalysis: updated });
    }

    // Update in orgGroupTestAnalysis
    const orgIdx = state.orgGroupTestAnalysis.findIndex(item => item.uid === action.item.uid);
    if (orgIdx >= 0) {
      const updated = [...state.orgGroupTestAnalysis];
      updated[orgIdx] = action.item;
      ctx.patchState({ orgGroupTestAnalysis: updated });
    }

    // Update in myTestAnalysis
    const myIdx = state.myTestAnalysis.findIndex(item => item.uid === action.item.uid);
    if (myIdx >= 0) {
      const updated = [...state.myTestAnalysis];
      updated[myIdx] = action.item;
      ctx.patchState({ myTestAnalysis: updated });
    }

    // Update in functionalGroupTestAnalysis
    const fgIdx = state.functionalGroupTestAnalysis.findIndex(item => item.uid === action.item.uid);
    if (fgIdx >= 0) {
      const updated = [...state.functionalGroupTestAnalysis];
      updated[fgIdx] = action.item;
      ctx.patchState({ functionalGroupTestAnalysis: updated });
    }
  }

  @Action(TestAnalysisActions.ClearCache)
  clearCache(ctx: StateContext<TestAnalysisStateModel>) {
    ctx.patchState({
      TestAnalysis: [],
      orgGroupTestAnalysis: [],
      functionalGroupTestAnalysis: [],
      myTestAnalysis: []
    });
  }

  @Selector()
  static getAllTestAnalysis(state: TestAnalysisStateModel) {
    return state.TestAnalysis;
  }

  @Selector()
  static getOrgGroupTestAnalysis(state: TestAnalysisStateModel) {
    return state.orgGroupTestAnalysis;
  }
  @Selector()
  static getMyTestAnalysis(state: TestAnalysisStateModel) {
    return state.myTestAnalysis;
  }

  @Selector()
  static getFgTestAnalysis(state: TestAnalysisStateModel) {
    return state.functionalGroupTestAnalysis;
  }
}
